import { isPointInRect } from '../hex/hitTest';
import type { Point, Tile } from '../hex/types';
import type { ActionRect, HexMapState } from '../render/renderer';
import type { AbstractModal } from '../../modal/AbstractModal';
import {
    ACTION__SIM_SPEED__FIVE,
    ACTION__SIM_SPEED__ONE,
    ACTION__SIM_SPEED__START,
    ACTION__SIM_SPEED__STOP,
    ACTION__SIM_SPEED__TEN,
    ACTION__SIM_SPEED__TWO,
    ACTION__SETTLEMENT__BUILD_LUMBERJACK,
    ACTION__SETTLEMENT__BUILD_HOUSE,
    ACTION__SETTLEMENT__BUILD_STONE_HOUSE,
    ACTION__SETTLEMENT__BUILD_FARM,
    ACTION__SETTLEMENT__BUILD_HUNTING_HUT,
    ACTION__SETTLEMENT__BUILD_FISHING_HUT,
    ACTION__SETTLEMENT__BUILD_SMOKEHOUSE,
    ACTION__SETTLEMENT__BUILD_WEAVER,
    ACTION__SETTLEMENT__BUILD_BLACKSMITH,
    ACTION__SETTLEMENT__BUILD_GOLD_MINE,
    ACTION__SETTLEMENT__BUILD_MINT,
    ACTION__SETTLEMENT__BUILD_CARAVAN_POST,
    ACTION__SETTLEMENT__BUILD_STABLE,
    ACTION__SETTLEMENT__BUILD_SAWMILL,
    ACTION__SETTLEMENT__FIRE_ASSISTANT,
    ACTION__SETTLEMENT__FIRE_FORESTER,
    ACTION__SETTLEMENT__FIRE_FARMER,
    ACTION__SETTLEMENT__FIRE_HUNTER,
    ACTION__SETTLEMENT__FIRE_FISHER,
    ACTION__SETTLEMENT__FIRE_SMOKER,
    ACTION__SETTLEMENT__FIRE_WEAVER,
    ACTION__SETTLEMENT__FIRE_SMITH,
    ACTION__SETTLEMENT__FIRE_GOLD_MINER,
    ACTION__SETTLEMENT__FIRE_MINTER,
    ACTION__SETTLEMENT__FIRE_LUMBERJACK,
    ACTION__SETTLEMENT__FIRE_SAWMILLER,
    ACTION__SETTLEMENT__HIRE_ASSISTANT,
    ACTION__SETTLEMENT__HIRE_FORESTER,
    ACTION__SETTLEMENT__HIRE_FARMER,
    ACTION__SETTLEMENT__HIRE_HUNTER,
    ACTION__SETTLEMENT__HIRE_FISHER,
    ACTION__SETTLEMENT__HIRE_SMOKER,
    ACTION__SETTLEMENT__HIRE_WEAVER,
    ACTION__SETTLEMENT__HIRE_SMITH,
    ACTION__SETTLEMENT__HIRE_GOLD_MINER,
    ACTION__SETTLEMENT__HIRE_MINTER,
    ACTION__SETTLEMENT__HIRE_BUILDER,
    ACTION__SETTLEMENT__HIRE_LUMBERJACK,
    ACTION__SETTLEMENT__HIRE_SAWMILLER,
    ACTION__SETTLEMENT__FIRE_BUILDER,
    ACTION__SETTLEMENT__BUILD_QUARRY,
    ACTION__SETTLEMENT__BUILD_MASONRY,
    ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT,
    ACTION__SETTLEMENT__BUILD_IRON_MINE,
    ACTION__SETTLEMENT__BUILD_SMELTER,
    ACTION__SETTLEMENT__BUILD_BARRACKS,
    ACTION__SETTLEMENT__HIRE_QUARRYMAN,
    ACTION__SETTLEMENT__FIRE_QUARRYMAN,
    ACTION__SETTLEMENT__HIRE_MASON,
    ACTION__SETTLEMENT__FIRE_MASON,
    ACTION__SETTLEMENT__HIRE_BURNER,
    ACTION__SETTLEMENT__FIRE_BURNER,
    ACTION__SETTLEMENT__HIRE_MINER,
    ACTION__SETTLEMENT__FIRE_MINER,
    ACTION__SETTLEMENT__HIRE_SMELTER,
    ACTION__SETTLEMENT__FIRE_SMELTER,
    ACTION__SETTLEMENT__HIRE_MILITIA,
    ACTION__SETTLEMENT__FIRE_MILITIA,
    ACTION__SETTLEMENT__CANCEL_CONSTRUCTION_PREFIX,
    ACTION__UI__LANGUAGE__EN,
    ACTION__UI__LANGUAGE__PL,
    ACTION__UI__DISMISS_ALERT,
    ACTION__UI__OPEN_SETTINGS,
    ACTION__UI__DISMISS_NOTIFICATION,
    ACTION__UI__TOGGLE_NOTIFICATIONS,
    ACTION__UI__TOGGLE_SPEED_MENU,
    ACTION__UI__TOGGLE_LANGUAGE_MENU,
    ACTION__UI__OPEN_BATTLE_REPORT,
    ACTION__UI__OPEN_SCOUTING_MISSION,
    ACTION__TILE__FOUND_SETTLEMENT,
} from './actionIds';
import type { ModalActionHandler } from '../action/modalActionTypes';
import { getBuildActionStatus } from '../action/buildActionMeta';
import { buildLumberjackAction } from '../action/build_lumberjack';
import { buildHouseAction } from '../action/build_house';
import { buildStoneHouseAction } from '../action/build_stone_house';
import { buildFarmAction } from '../action/build_farm';
import { buildHuntingHutAction } from '../action/build_hunting_hut';
import { buildFishingHutAction } from '../action/build_fishing_hut';
import { buildSmokehouseAction } from '../action/build_smokehouse';
import { buildWeaverAction } from '../action/build_weaver';
import { buildBlacksmithAction } from '../action/build_blacksmith';
import { buildGoldMineAction } from '../action/build_gold_mine';
import { buildMintAction } from '../action/build_mint';
import { buildCaravanPostAction } from '../action/build_caravan_post';
import { buildStableAction } from '../action/build_stable';
import { fireAssistantAction } from '../action/fire_assistant';
import { fireForesterAction } from '../action/fire_forester';
import { fireFarmerAction } from '../action/fire_farmer';
import { fireHunterAction } from '../action/fire_hunter';
import { fireFisherAction } from '../action/fire_fisher';
import { fireSmokerAction } from '../action/fire_smoker';
import { fireWeaverAction } from '../action/fire_weaver';
import { fireSmithAction } from '../action/fire_smith';
import { fireGoldMinerAction } from '../action/fire_gold_miner';
import { fireMinterAction } from '../action/fire_minter';
import { fireLumberjackAction } from '../action/fire_lumberjack';
import { fireSawmillerAction } from '../action/fire_sawmiller';
import { fireBuilderAction } from '../action/fire_builder';
import { fireQuarrymanAction } from '../action/fire_quarryman';
import { fireMasonAction } from '../action/fire_mason';
import { fireBurnerAction } from '../action/fire_burner';
import { fireMinerAction } from '../action/fire_miner';
import { fireSmelterAction } from '../action/fire_smelter';
import { fireMilitiaAction } from '../action/fire_militia';
import { cancelConstructionAction } from '../action/cancel_construction';
import { hireLumberjackAction } from '../action/hire_lumberjack';
import { hireAssistantAction } from '../action/hire_assistant';
import { hireForesterAction } from '../action/hire_forester';
import { hireFarmerAction } from '../action/hire_farmer';
import { hireHunterAction } from '../action/hire_hunter';
import { hireFisherAction } from '../action/hire_fisher';
import { hireSmokerAction } from '../action/hire_smoker';
import { hireWeaverAction } from '../action/hire_weaver';
import { hireSmithAction } from '../action/hire_smith';
import { hireGoldMinerAction } from '../action/hire_gold_miner';
import { hireMinterAction } from '../action/hire_minter';
import { buildSawmillAction } from '../action/build_sawmill';
import { hireSawmillerAction } from '../action/hire_sawmiller';
import { hireBuilderAction } from '../action/hire_builder';
import { buildQuarryAction } from '../action/build_quarry';
import { buildMasonryAction } from '../action/build_masonry';
import { hireQuarrymanAction } from '../action/hire_quarryman';
import { hireMasonAction } from '../action/hire_mason';
import { buildCharcoalPitAction } from '../action/build_charcoal_pit';
import { buildIronMineAction } from '../action/build_iron_mine';
import { buildSmelterAction } from '../action/build_smelter';
import { buildBarracksAction } from '../action/build_barracks';
import { hireBurnerAction } from '../action/hire_burner';
import { hireMinerAction } from '../action/hire_miner';
import { hireSmelterAction } from '../action/hire_smelter';
import { hireMilitiaAction } from '../action/hire_militia';
import { foundSettlementAction } from '../action/found_settlement';
import { SettingsModal } from '../../modal/SettingsModal';
import { BattleReportModal } from '../../modal/battle/BattleReportModal';
import { ScoutingMissionModal } from '../../modal/scouting/ScoutingMissionModal';
import { saveGameConfig, setGameConfig } from '../../config/gameConfig';
import type { Language } from '../../i18n/translations';
import { t } from '../../i18n/translations';
import { getSettlement } from '../action/settlementActionUtils';
import { isTutorialActionAllowed } from '../../tutorials/runtime';

const modalActionRegistry = new Map<string, ModalActionHandler>();

export function registerModalAction(id: string, handler: ModalActionHandler): void {
    modalActionRegistry.set(id, handler);
}

export function getActionBarActionAtPoint(state: HexMapState, point: Point): ActionRect | null {
    return (
        state.actionBarActions.find(
            (action) => action.enabled && isPointInRect(point, action.rect),
        ) || null
    );
}

export function applyActionBarAction(state: HexMapState, actionId: string): boolean {
    if (!isTutorialActionAllowed(state, actionId)) return false;
    switch (actionId) {
        case ACTION__SIM_SPEED__STOP:
            if (!state.simulation.running) return false;
            state.simulation.running = false;
            state.simulation.accumulator = 0;
            return true;
        case ACTION__SIM_SPEED__START:
            if (state.simulation.running) return false;
            state.simulation.running = true;
            return true;
        case ACTION__SIM_SPEED__ONE:
            if (state.simulation.speed === 1) return false;
            state.simulation.speed = 1;
            state.overlay.speedMenuOpen = false;
            return true;
        case ACTION__SIM_SPEED__TWO:
            if (state.simulation.speed === 2) return false;
            state.simulation.speed = 2;
            state.overlay.speedMenuOpen = false;
            return true;
        case ACTION__SIM_SPEED__FIVE:
            if (state.simulation.speed === 5) return false;
            state.simulation.speed = 5;
            state.overlay.speedMenuOpen = false;
            return true;
        case ACTION__SIM_SPEED__TEN:
            if (state.simulation.speed === 10) return false;
            state.simulation.speed = 10;
            state.overlay.speedMenuOpen = false;
            return true;
        case ACTION__UI__OPEN_SETTINGS: {
            const existing = state.modals.find((modal) => modal.id === 'settings_modal');
            if (existing) return false;
            const rect = { x: state.pointer.x - 120, y: state.pointer.y - 60, w: 320, h: 360 };
            state.modals.push(new SettingsModal(rect));
            state.activeModalId = 'settings_modal';
            return true;
        }
        case ACTION__UI__LANGUAGE__EN:
            state.overlay.languageMenuOpen = false;
            return setLanguage(state, 'en');
        case ACTION__UI__LANGUAGE__PL:
            state.overlay.languageMenuOpen = false;
            return setLanguage(state, 'pl');
        case ACTION__UI__TOGGLE_NOTIFICATIONS:
            state.overlay.showNotifications = !state.overlay.showNotifications;
            return true;
        case ACTION__UI__TOGGLE_SPEED_MENU:
            state.overlay.speedMenuOpen = !state.overlay.speedMenuOpen;
            if (state.overlay.speedMenuOpen) state.overlay.languageMenuOpen = false;
            return true;
        case ACTION__UI__TOGGLE_LANGUAGE_MENU:
            state.overlay.languageMenuOpen = !state.overlay.languageMenuOpen;
            if (state.overlay.languageMenuOpen) state.overlay.speedMenuOpen = false;
            return true;
        default:
            if (actionId.startsWith(`${ACTION__UI__OPEN_SCOUTING_MISSION}:`)) {
                const missionId = actionId.slice(`${ACTION__UI__OPEN_SCOUTING_MISSION}:`.length);
                const mission = state.scoutingMissions.find((entry) => entry.id === missionId);
                if (!mission) return false;
                const modalId = `scouting_mission_${missionId}`;
                const existing = state.modals.find((modal) => modal.id === modalId);
                if (existing) return false;
                const tile =
                    state.modals.find((modal) => modal.tile.id === mission.targetTileId)?.tile ||
                    state.hover ||
                    buildFallbackTile(mission.targetTileId);
                const rect = { x: state.pointer.x - 120, y: state.pointer.y - 60, w: 320, h: 360 };
                state.modals.push(new ScoutingMissionModal(rect, tile, missionId));
                state.activeModalId = modalId;
                return true;
            }
            if (actionId.startsWith(`${ACTION__UI__OPEN_BATTLE_REPORT}:`)) {
                const reportId = actionId.slice(`${ACTION__UI__OPEN_BATTLE_REPORT}:`.length);
                const modalId = `battle_report_${reportId}`;
                const existing = state.modals.find((modal) => modal.id === modalId);
                if (existing) return false;
                const rect = { x: state.pointer.x - 120, y: state.pointer.y - 60, w: 320, h: 360 };
                state.modals.push(new BattleReportModal(rect, reportId));
                state.activeModalId = modalId;
                return true;
            }
            if (actionId.startsWith(`${ACTION__UI__DISMISS_NOTIFICATION}:`)) {
                const id = actionId.slice(`${ACTION__UI__DISMISS_NOTIFICATION}:`.length);
                state.dismissedNotifications.add(id);
                return true;
            }
            if (actionId.startsWith(`${ACTION__UI__DISMISS_ALERT}:`)) {
                const id = actionId.slice(`${ACTION__UI__DISMISS_ALERT}:`.length);
                state.alerts = state.alerts.filter((alert) => alert.id !== id);
                return true;
            }
            return false;
    }
}

function setLanguage(state: HexMapState, language: Language): boolean {
    if (state.config.ui.language === language) return false;
    state.config = { ...state.config, ui: { ...state.config.ui, language } };
    state.settingsEditor.text = JSON.stringify(state.config, null, 2);
    setGameConfig(state.config);
    saveGameConfig(state.config);
    return true;
}

export function applyModalAction(
    state: HexMapState,
    modal: AbstractModal,
    actionId: string,
): boolean {
    if (actionId.startsWith(ACTION__SETTLEMENT__CANCEL_CONSTRUCTION_PREFIX)) {
        const id = actionId.slice(ACTION__SETTLEMENT__CANCEL_CONSTRUCTION_PREFIX.length);
        return cancelConstructionAction(id)(state, modal);
    }
    const handler = modalActionRegistry.get(actionId);
    if (!handler) return false;
    const result = handler(state, modal);
    if (result) return true;
    const settlement = getSettlement(state, modal);
    if (!settlement) return false;
    const buildInfo = getBuildActionStatus(state, settlement, actionId);
    if (!buildInfo || buildInfo.enabled) return false;
    if (buildInfo.reasonKey) {
        const message = t(
            state.config.ui.language,
            'overlay.alertBuildFailed',
            t(state.config.ui.language, buildInfo.labelKey),
            t(state.config.ui.language, buildInfo.reasonKey, ...(buildInfo.reasonArgs || [])),
        );
        state.alertIdCounter += 1;
        state.alerts.push({ id: `alert_${state.alertIdCounter}`, message, level: 'error' });
        return true;
    }
    return false;
}

function buildFallbackTile(id: string): Tile {
    const parts = id.split('_');
    const q = Number(parts[1] ?? 0);
    const r = Number(parts[2] ?? 0);
    const s = Number(parts[3] ?? 0);
    return {
        id,
        position: {
            q: Number.isFinite(q) ? q : 0,
            r: Number.isFinite(r) ? r : 0,
            s: Number.isFinite(s) ? s : 0,
        },
        biome: { type: 'plains' },
        resources: {},
    };
}

registerModalAction(ACTION__SETTLEMENT__BUILD_LUMBERJACK, buildLumberjackAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_HOUSE, buildHouseAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_STONE_HOUSE, buildStoneHouseAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_FARM, buildFarmAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_HUNTING_HUT, buildHuntingHutAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_FISHING_HUT, buildFishingHutAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_SMOKEHOUSE, buildSmokehouseAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_WEAVER, buildWeaverAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_BLACKSMITH, buildBlacksmithAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_GOLD_MINE, buildGoldMineAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_MINT, buildMintAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_CARAVAN_POST, buildCaravanPostAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_STABLE, buildStableAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_LUMBERJACK, hireLumberjackAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_ASSISTANT, hireAssistantAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_LUMBERJACK, fireLumberjackAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_ASSISTANT, fireAssistantAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_FORESTER, hireForesterAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_FORESTER, fireForesterAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_FARMER, hireFarmerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_FARMER, fireFarmerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_HUNTER, hireHunterAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_HUNTER, fireHunterAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_FISHER, hireFisherAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_FISHER, fireFisherAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_SMOKER, hireSmokerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_SMOKER, fireSmokerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_WEAVER, hireWeaverAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_WEAVER, fireWeaverAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_SMITH, hireSmithAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_SMITH, fireSmithAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_GOLD_MINER, hireGoldMinerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_GOLD_MINER, fireGoldMinerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_MINTER, hireMinterAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_MINTER, fireMinterAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_SAWMILL, buildSawmillAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_SAWMILLER, hireSawmillerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_SAWMILLER, fireSawmillerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_BUILDER, hireBuilderAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_BUILDER, fireBuilderAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_QUARRY, buildQuarryAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_MASONRY, buildMasonryAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_QUARRYMAN, hireQuarrymanAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_QUARRYMAN, fireQuarrymanAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_MASON, hireMasonAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_MASON, fireMasonAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT, buildCharcoalPitAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_IRON_MINE, buildIronMineAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_SMELTER, buildSmelterAction);
registerModalAction(ACTION__SETTLEMENT__BUILD_BARRACKS, buildBarracksAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_BURNER, hireBurnerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_BURNER, fireBurnerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_MINER, hireMinerAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_MINER, fireMinerAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_SMELTER, hireSmelterAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_SMELTER, fireSmelterAction);
registerModalAction(ACTION__SETTLEMENT__HIRE_MILITIA, hireMilitiaAction);
registerModalAction(ACTION__SETTLEMENT__FIRE_MILITIA, fireMilitiaAction);
registerModalAction(ACTION__TILE__FOUND_SETTLEMENT, foundSettlementAction);
